import React, { useEffect, useMemo } from "react";
import FormItem from "@components/FormItem/FormItem";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { zodResolver } from "@hookform/resolvers/zod";
import type {
  DryBulkResourceOwner,
  DryBulkResourceType,
} from "@services/dry-bulk-nomination/types";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  getResourceSchema,
  LIMITS,
  type ResourceFormData,
} from "../schema/dryBulkNominationSchema";
import DryBulkDrawerFields from "./DryBulkDrawerFields";

const LANG_BASE = "vessel-nomination.dry-bulk.add.resources";

interface Props {
  isOpen: boolean;
  owner: DryBulkResourceOwner;
  resource?: ResourceFormData;
  /** Types still free in this section (max one Geared and one Gearless, VN-08). */
  availableTypes: DryBulkResourceType[];
  onClose: () => void;
  onSave: (resource: ResourceFormData) => void;
}

const NUMBER_FIELDS = [
  "craneCount",
  "craneCapacityMt",
  "craneSwl",
  "grabCount",
  "grabSizeCbm",
  "grabSwl",
] as const;
const CONDITION_FIELDS = ["cranesGoodCondition", "grabsGoodCondition"] as const;

/** SA-7: cranes block, then grabs block; each block is its numbers followed by the good-condition checkbox. */
const GEARED_BLOCKS = [
  [
    { field: "craneCount", digits: LIMITS.COUNT_DIGITS },
    { field: "craneCapacityMt", digits: LIMITS.MEASURE_DIGITS, unit: "mt" },
    { field: "craneSwl", digits: LIMITS.MEASURE_DIGITS },
    { field: "cranesGoodCondition" },
  ],
  [
    { field: "grabCount", digits: LIMITS.COUNT_DIGITS },
    { field: "grabSizeCbm", digits: LIMITS.MEASURE_DIGITS, unit: "cbm" },
    { field: "grabSwl", digits: LIMITS.MEASURE_DIGITS },
    { field: "grabsGoodCondition" },
  ],
] as const;

/** An undefined value turns the number input uncontrolled, so it would keep the previous number after a reset. */
const BLANK_NUMBERS = Object.fromEntries(
  [...NUMBER_FIELDS, "resourceCount"].map((field) => [field, ""]),
) as unknown as Partial<ResourceFormData>;
const UNCHECKED = { cranesGoodCondition: false, grabsGoodCondition: false };
const withBlankNumbers = (resource: ResourceFormData): ResourceFormData => ({
  ...resource,
  ...Object.fromEntries(CONDITION_FIELDS.map((field) => [field, resource[field] ?? false])),
  ...Object.fromEntries(
    Object.keys(BLANK_NUMBERS).map((field) => [
      field,
      resource[field as keyof ResourceFormData] ?? "",
    ]),
  ),
});

/** VN-08: Geared → cranes and grabs; Gearless → resource count. Counts ≤ 3 digits, measures ≤ 6. */
const ResourceDrawer: React.FC<Props> = ({
  isOpen,
  owner,
  resource,
  availableTypes,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const schema = useMemo(() => getResourceSchema(t), [t]);
  const empty = useMemo<ResourceFormData>(
    () => ({
      owner,
      type: (availableTypes[0] ?? "GEARED") as DryBulkResourceType,
      ...BLANK_NUMBERS,
      ...UNCHECKED,
    }),
    [owner, availableTypes],
  );
  const form = useForm<ResourceFormData>({
    mode: "onTouched",
    resolver: zodResolver(schema),
    defaultValues: empty,
  });

  useEffect(() => {
    if (isOpen) form.reset(resource ? withBlankNumbers(resource) : empty);
  }, [form, isOpen, resource, empty]);

  const type = form.watch("type");
  const typeOptions = (resource ? [resource.type] : availableTypes).map((value) => ({
    value,
    label: t(`${LANG_BASE}.types.${value}`),
  }));

  return (
    <div data-testid="dry-bulk-resource-drawer">
      <FormProvider {...form}>
        <SideDrawer
          isOpen={isOpen}
          onClose={onClose}
          onSubmit={form.handleSubmit((values) => onSave(values))}
          title={t(`${LANG_BASE}.${resource ? "edit" : "add"}`)}
          submitLabel={t(`${LANG_BASE}.${resource ? "save" : "add-submit"}`)}
          cancelLabel={t("common.cancel")}
        >
          <DryBulkDrawerFields testId="dry-bulk-resource-fields">
            <FormItem<ResourceFormData, "type">
              required
              testId="dry-bulk-resource-type"
              name="type"
              type="select"
              label={t(`${LANG_BASE}.type`)}
              options={typeOptions}
              disabled={!!resource}
            />
            {type === "GEARED" &&
              GEARED_BLOCKS.flat().map((item) =>
                "digits" in item ? (
                  <FormItem<ResourceFormData, typeof item.field>
                    key={item.field}
                    testId={`dry-bulk-resource-${item.field}`}
                    name={item.field}
                    type="number"
                    label={t(`${LANG_BASE}.fields.${item.field}`)}
                    maxDigits={item.digits}
                    decimals={0}
                    adornment={"unit" in item ? t(`${LANG_BASE}.units.${item.unit}`) : undefined}
                  />
                ) : (
                  <FormItem<ResourceFormData, typeof item.field>
                    key={item.field}
                    testId={`dry-bulk-resource-${item.field}`}
                    name={item.field}
                    type="checkbox"
                    label={t(`${LANG_BASE}.fields.${item.field}`)}
                  />
                ),
              )}
            {type === "GEARLESS" && (
              <FormItem<ResourceFormData, "resourceCount">
                required
                testId="dry-bulk-resource-resourceCount"
                name="resourceCount"
                type="number"
                label={t(`${LANG_BASE}.fields.resourceCount`)}
                maxDigits={LIMITS.COUNT_DIGITS}
                decimals={0}
              />
            )}
          </DryBulkDrawerFields>
        </SideDrawer>
      </FormProvider>
    </div>
  );
};

export default ResourceDrawer;
